/* eslint-disable etc/no-commented-out-code */
import { Navigate, useLocation, useRoutes } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { AxiosError } from "axios";
import OptionsLayout from "./layouts/dashboard";
import FaqComponent from "./pages/FAQ/Faq";
import NotFound from "./pages/Page404";
import SomeOtherPage from "./pages/SomeOtherPage";
import ValuationOverview from "./pages/Valuation/ValuationOverview";
import AllEmployees from "./pages/allEmployees/AllEmployees";
import EmployeeCreationImportPage from "./pages/allEmployees/EmployeeCreationImportPage";
import McpEmployeeImportPage from "./pages/allEmployees/McpEmployeeImportPage";
import EmployeeHistoryImportPage from "./pages/allEmployees/EmployeeHistoryImportPage";
import AllPlans from "./pages/allPlans";
import CreateViewOrEditPlan from "./pages/allPlans/CreateViewOrEditPlan";
import SinglePlanView from "./pages/allPlans/SinglePlanView";
import CreateCompany from "./pages/company/createCompany";
import { CompanyProfile } from "./pages/companyProfile/CompanyProfile";
import DocumentManager from "./pages/document/DocumentManager";
import EmployeeDetails from "./pages/employeeDetails/EmployeeDetails";
import Modelling from "./pages/esopModeling";
import CompensationModelling from "./pages/esopModeling/compModelling";
import CompanyDetails from "./pages/esopModeling/companyDetails";
import HiringPlan from "./pages/esopModeling/hiringPlan";
import AddOrEditPoolSize from "./pages/esopOverview/AddOrEditPoolSize";
import EsopOverview from "./pages/esopOverview/EsopOverview";
import GrantsImport from "./pages/esopOverview/GrantsImport";
import GrantDetails from "./pages/esopOverview/HistoricOnboarding";
import HistoricExerciseImport from "./pages/esopOverview/HistoricExerciseImport";
import LoginPage from "./pages/login/loginPage";
import { Milestones } from "./pages/milestones";
import { CompleteMilestone } from "./pages/milestones/CompleteMilestone";
import { ViewMilestone } from "./pages/milestones/ViewMilestone";
import BuybackIndex from "./pages/optionBuyback";
import CompleteBuyback from "./pages/optionBuyback/CompleteBuyback";
import CreateExerciseEvent from "./pages/optionBuyback/CreateExerciseEvent";
import CreateExerciseFlow from "./pages/optionBuyback/CreateExerciseFlow";
import CreateLiquidationFlow from "./pages/optionBuyback/CreateLiquidationFlow";
import CreateOptionBuyback from "./pages/optionBuyback/CreateOptionBuyback";
import LiquidationView from "./pages/optionBuyback/LiquidationView";
import StartBuyback from "./pages/optionBuyback/StartBuyback";
import ViewOptionBuybackDetails from "./pages/optionBuyback/ViewOptionBuybackDetails";
import { OptionSplit } from "./pages/optionSplit/OptionSplit";
import { Playground } from "./pages/playground";
import SiteSettings from "./pages/settings/SiteSettings";
import UserProfilePage from "./pages/userProfile/UserProfilePage";
import UserRoles from "./pages/usersRoles/UserRoles";
import { VestingTemplates } from "./pages/vestingSchedules";
import { useAuthStore } from "./store";
import { usePermissionStore } from "./store/permissionStore";
import { determineUserAccessToResource } from "./utils/auth";
import { Action, Permission, Resource } from "./utils/interfaces/Companies";
import UploadGrantLetters from "./pages/esopOverview/UploadGrantLetters";

import SyncDetails from "./pages/syncs/SyncDetails";
import IndividualSyncDetails from "./pages/syncs/IndividualSyncDetails";
import PendingRequestsApproval from "./pages/syncs/PendingRequestsApproval";
import Reports from "./pages/reports/Reports";
import { CustomReportBuilder } from "./pages/reports/customReportBuilder";
import AddGrantDetails from "./pages/grants";
import BulkResignation from "./pages/employeeDetails/BulkResignation";
import BulkInitiateResignation from "./pages/employeeDetails/BulkInitiateResignation";
import BulkCompleteResignations from "./pages/allEmployees/BulkCompleteResignations";
import TaxImport from "./pages/employeeDetails/TaxImport";
import { useCheckHeartBeat, useRefetchAccessToken } from "./queries/idServer";
import { useGetUserContext, useGetUserRoleAndPermission } from "./queries";
import { Center, SecondaryCTAButton, VStack } from "./components/utils";
import ManagerDashboard from "./pages/managerDashboard/ManagerDashboard";
import { isEquionSessionInvalid, redirectToAdminSignIn } from "./api/idServer";
import { RedirectLogin } from "./pages/login/RedirectLogin";
import Loader from "./shared/Loader";
import AddExerciseDetails from "./pages/transactions/Exercise";
import CompanyProfileStepper from "./pages/companyProfile/CompanyProfileStepper";
import EmployeeMetaData from "./pages/employeeDetails/employee-meta-data/EmployeeMetaData";
import BlackScholes from "./pages/black-scholes/BlackScholes";
import BulkGrantUpdate from "./pages/grants/BulkGrantUpdate";
import CustomEmailCampaign from "./pages/allEmployees/customEmail";
import BulkUpdateGrantIds from "./pages/grants/BulkUpdateGrantIds";
import BulkExtendExercise from "./pages/esopOverview/BulkExtendExercise";
import AllocationMatrixContainer from "./pages/companyProfile/allocationMatrix";
import RepricingHistoryPage from "./pages/companyProfile/optionsRepricing/RepricingHistoryPage";
import OptionsRepricingPage from "./pages/companyProfile/optionsRepricing";
import EmployeeFormPage from "./pages/employees/EmployeeFormPage";
import BillingDashboard from "./pages/billing/BillingDashboard";
import { useIsBillingUser } from "./utils/useIsBillingUser";
import OperationalControls from "./pages/operationalControls/OperationalControls";
import CreateOperationalControl from "./pages/operationalControls/CreateOperationalControl";
import { useIsRulezeroUser } from "./utils/useIsRulezeroUser";
import OperationalControlAdmissionGate from "./components/shared/OperationalControlAdmissionGate";
import AccessRestricted from "./pages/accessRestricted/AccessRestricted";
import { isOperationalControlFeatureEnabled } from "./utils/operationalControlFeature";
import { PendingHissaCompanySwitch } from "./components/shared/HandleHissaRedirect";
import { McpAuthorize } from "./pages/login/McpAuthorize";
import { optionsAdminAuth } from "./api/adminAuthMode";
import { clearOptionsSession } from "./api/adminAuth";
import { getOptionsSession } from "./api/optionsSession";
import { safeReturnUrl } from "./utils/returnUrl";

function RequestError({
  title,
  onRetry,
}: {
  title: string;
  onRetry: () => void;
}) {
  return (
    <Center className="min-h-screen bg-slate-light px-4">
      <VStack className="w-full max-w-md items-center gap-4 rounded-lg bg-white p-6 text-center shadow-card">
        <h1 className="text-xl leading-7 font-semibold text-headerColor">
          {title}
        </h1>
        <p className="text-sm leading-5 text-secondaryText">
          Check your connection and try again.
        </p>
        <SecondaryCTAButton onClick={onRetry}>Try again</SecondaryCTAButton>
      </VStack>
    </Center>
  );
}

export default function Router() {
  const operationalControlEnabled = isOperationalControlFeatureEnabled();
  const location = useLocation();
  const { pathname } = location;
  const isPublicRoute =
    pathname === "/access-restricted" ||
    pathname === "/404" ||
    pathname === "/mcp/authorize" ||
    (optionsAdminAuth && pathname === "/login");
  const { isAuthenticated, accessToken, setAuth } = useAuthStore();
  const isBillingUser = useIsBillingUser();
  const isRulezeroUser = useIsRulezeroUser();
  const { permission, setPermission } = usePermissionStore();
  const bootstrappedGrant = useRef<string | null>(null);
  const [contextFetchedForGrant, setContextFetchedForGrant] = useState<
    string | null
  >(null);
  const [permissionFetchedForGrant, setPermissionFetchedForGrant] = useState<
    string | null
  >(null);
  const {
    data: accessTokenData,
    error: accessTokenError,
    isError: accessTokenFailed,
    refetch: retryAccessToken,
  } = useRefetchAccessToken(!isPublicRoute && !optionsAdminAuth);
  const localSession = useQuery(
    ["optionsAdminSession", accessToken],
    () => getOptionsSession(accessToken!),
    {
      enabled:
        optionsAdminAuth &&
        Boolean(accessToken) &&
        pathname !== "/mcp/authorize",
      retry: false,
      refetchOnWindowFocus: true,
    }
  );
  useEffect(() => {
    if (
      optionsAdminAuth &&
      localSession.isError &&
      (localSession.error as AxiosError)?.response?.status === 401
    ) {
      clearOptionsSession();
    }
  }, [localSession.isError, localSession.error]);
  const verifiedGrant = optionsAdminAuth
    ? localSession.data?.userId
      ? accessToken
      : undefined
    : accessTokenData;
  const hasBootstrappedSession = Boolean(
    verifiedGrant &&
      bootstrappedGrant.current === verifiedGrant &&
      isAuthenticated &&
      accessToken
  );
  const shouldCheckHeartbeat =
    !optionsAdminAuth && hasBootstrappedSession && window.self === window.top;
  const {
    data: heartbeatData,
    error: heartbeatError,
    isError: heartbeatFailed,
    refetch: retryHeartbeat,
  } = useCheckHeartBeat(shouldCheckHeartbeat);
  const {
    data: userContext,
    isError: userContextFailed,
    refetch: getUserContext,
  } = useGetUserContext();
  const {
    data: permissionData,
    isError: permissionFailed,
    refetch: getPermission,
  } = useGetUserRoleAndPermission();

  const loadUserContext = useCallback(
    async (grant: string) => {
      await getUserContext();
      if (bootstrappedGrant.current === grant) {
        setContextFetchedForGrant(grant);
      }
    },
    [getUserContext]
  );

  const loadPermission = useCallback(
    async (grant: string) => {
      const result = await getPermission();
      if (bootstrappedGrant.current !== grant) return;
      if (result.data?.role) {
        setPermission(result.data as Permission);
      }
      setPermissionFetchedForGrant(grant);
    },
    [getPermission, setPermission]
  );

  useEffect(() => {
    if (
      !optionsAdminAuth &&
      accessTokenFailed &&
      isEquionSessionInvalid(accessTokenError)
    ) {
      redirectToAdminSignIn();
    }
  }, [accessTokenError, accessTokenFailed]);

  useEffect(() => {
    if (
      !optionsAdminAuth &&
      heartbeatFailed &&
      isEquionSessionInvalid(heartbeatError)
    ) {
      redirectToAdminSignIn();
    }
  }, [heartbeatError, heartbeatFailed]);

  useEffect(() => {
    if (verifiedGrant && bootstrappedGrant.current !== verifiedGrant) {
      bootstrappedGrant.current = verifiedGrant;
      setContextFetchedForGrant(null);
      setPermissionFetchedForGrant(null);
      sessionStorage.setItem("accesstoken", verifiedGrant);
      setAuth((state) => ({
        ...state,
        accessToken: verifiedGrant,
        isAuthenticated: true,
      }));
      if (optionsAdminAuth && localSession.data?.defaultCompanyId) {
        sessionStorage.setItem("companyId", localSession.data.defaultCompanyId);
      }
      loadUserContext(verifiedGrant);
    }
  }, [verifiedGrant, loadUserContext, localSession.data, setAuth]);

  useEffect(() => {
    if (
      !hasBootstrappedSession ||
      !verifiedGrant ||
      contextFetchedForGrant !== verifiedGrant ||
      userContextFailed ||
      !userContext
    )
      return;
    if (userContext.defaultCompanyId && !sessionStorage.getItem("companyId")) {
      sessionStorage.setItem("companyId", userContext.defaultCompanyId);
    }
    loadPermission(verifiedGrant);
  }, [
    verifiedGrant,
    contextFetchedForGrant,
    hasBootstrappedSession,
    loadPermission,
    userContext,
    userContextFailed,
  ]);

  const routes = useRoutes([
    // {
    //   path: "/mcp/authorize",
    //   element: <McpAuthorize />,
    // },
    // {
    //   path: "/mcp/upload",
    //   element: isAuthenticated ? <McpEmployeeImportPage /> : <RedirectLogin />,
    // },
    {
      path: "/login",
      element: optionsAdminAuth ? (
        localSession.data ? (
          <Navigate
            to={
              safeReturnUrl(
                new URLSearchParams(location.search).get("returnUrl")
              ) || "/"
            }
            replace
          />
        ) : accessToken && !localSession.isError ? (
          <Loader />
        ) : (
          <LoginPage />
        )
      ) : !isAuthenticated ? (
        <RedirectLogin />
      ) : (
        <Navigate to="/" replace />
      ),
    },
    {
      path: "/access-restricted",
      element: operationalControlEnabled ? (
        <AccessRestricted />
      ) : (
        <Navigate to="/" replace />
      ),
    },
    {
      path: "/company/createCompany",
      element: isAuthenticated ? (
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.Company,
          Action.Create
        ) ? (
          <CreateCompany />
        ) : (
          <NotFound />
        )
      ) : (
        <RedirectLogin />
      ),
    },
    {
      path: "/options",
      element: isAuthenticated ? (
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.All,
          Action.Read
        ) ? (
          <OptionsLayout />
        ) : permission?.role === "MANAGER" ? (
          <Navigate to="/manager-portal/dashboard" replace />
        ) : (
          <RedirectLogin />
        )
      ) : (
        <RedirectLogin />
      ),
      children: [
        {
          path: "operational-controls",
          element:
            operationalControlEnabled && isRulezeroUser ? (
              <OperationalControls />
            ) : (
              <NotFound />
            ),
        },
        {
          path: "operational-controls/new",
          element:
            operationalControlEnabled && isRulezeroUser ? (
              <CreateOperationalControl />
            ) : (
              <NotFound />
            ),
        },
        {
          path: "operational-controls/:controlId/edit",
          element:
            operationalControlEnabled && isRulezeroUser ? (
              <CreateOperationalControl />
            ) : (
              <NotFound />
            ),
        },
        { path: "employees/new", element: <EmployeeFormPage /> },
        { path: "employees/:employeeId/edit", element: <EmployeeFormPage /> },
        { path: "esopOverview", element: <EsopOverview /> },
        {
          path: "plan/:mode?/:id?",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateViewOrEditPlan />
          ) : (
            <NotFound />
          ),
        },
        { path: "completeMilestone/:id", element: <CompleteMilestone /> },
        { path: "viewMilestone/:id", element: <ViewMilestone /> },
        {
          path: "add-edit-pool",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <AddOrEditPoolSize />
          ) : (
            <NotFound />
          ),
        },

        {
          path: "grant/:mode?/:id?",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Read
          ) ? (
            <AddGrantDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "addAllocationMatrix",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <AllocationMatrixContainer />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "optionsRepricing",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <RepricingHistoryPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "optionsRepricing/new",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <OptionsRepricingPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "optionsRepricing/edit/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <OptionsRepricingPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "allEmployees",
          element: <AllEmployees />,
          children: [
            {
              path: "employeeDetails/:id",
              element: determineUserAccessToResource(
                permission?.aclList || [],
                Resource.All,
                Action.Read
              ) ? (
                <EmployeeDetails />
              ) : (
                <NotFound />
              ),
            },
          ],
        },
        {
          path: "bulkResignation",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <BulkResignation />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "bulkInitiateResignation",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <BulkInitiateResignation />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "bulkCompleteResignations",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <BulkCompleteResignations />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employees/custom-email",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CustomEmailCampaign />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employeeDetails/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <EmployeeDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "onboarding/historic",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <GrantDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "exercise/historic-import",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Create
          ) ? (
            <HistoricExerciseImport />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "onboarding/grants",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Create
          ) ? (
            <GrantsImport />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "grants/createGrantTransactions",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.All
          ) ? (
            <BulkGrantUpdate />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "grants/bulk-update-ids",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Edit
          ) ? (
            <BulkUpdateGrantIds />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "grants/bulk-extend-exercise",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Edit
          ) ? (
            <BulkExtendExercise />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "onboarding/uploadGrantLetters",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Grant,
            Action.Create
          ) ? (
            <UploadGrantLetters />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "onboarding/employees",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Employee,
            Action.Create
          ) ? (
            <EmployeeCreationImportPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employeeHistoryImport",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.Employee,
            Action.Edit
          ) ? (
            <EmployeeHistoryImportPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "allPlans",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <AllPlans />
          ) : (
            <NotFound />
          ),
          children: [
            {
              path: "planView/:id",
              element: determineUserAccessToResource(
                permission?.aclList || [],
                Resource.All,
                Action.Read
              ) ? (
                <SinglePlanView />
              ) : (
                <NotFound />
              ),
            },
          ],
        },
        {
          path: "planView/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <SinglePlanView />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "vestingSchedules",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <VestingTemplates />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "milestones",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <Milestones />
          ) : (
            <NotFound />
          ),
          children: [
            { path: "completeMilestone/:id", element: <CompleteMilestone /> },
            { path: "viewMilestone/:id", element: <ViewMilestone /> },
          ],
        },
        {
          path: "reports",
          element:
            permission?.role !== "CHECKER" &&
            determineUserAccessToResource(
              permission?.aclList || [],
              Resource.All,
              Action.Read
            ) ? (
              <Reports />
            ) : (
              <NotFound />
            ),
        },
        {
          path: "reports/builder/:id?",
          element:
            permission?.role !== "CHECKER" &&
            determineUserAccessToResource(
              permission?.aclList || [],
              Resource.All,
              Action.Read
            ) ? (
              <CustomReportBuilder />
            ) : (
              <NotFound />
            ),
        },
        {
          path: "esopTransactions",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <BuybackIndex />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "esopTransactions/exerciseRequests",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <BuybackIndex />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-option-buyback",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateOptionBuyback />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-exercise-flow",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateExerciseFlow />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-exercise-event",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <AddExerciseDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-exercise-liquidation-flow",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateExerciseFlow />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-liquidation-flow",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateLiquidationFlow />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "liquidation-details/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <LiquidationView />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "edit-options-buyback/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateOptionBuyback />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "edit-exercise-flow/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateExerciseFlow />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "exercise-event/:mode/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <AddExerciseDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "edit-liquidation-flow/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CreateLiquidationFlow />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "start-buy-back/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <StartBuyback />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "complete-buy-back/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CompleteBuyback />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "view-option-buy-back/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <ViewOptionBuybackDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "playground",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <Playground />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "optionSplit",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Edit
          ) ? (
            <OptionSplit />
          ) : (
            <SomeOtherPage />
          ),
        },
        {
          path: "usersRoles",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <UserRoles />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "someOtherPage",
          element: isAllowed("someOtherPage") ? (
            <SomeOtherPage />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "valuationReports",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <ValuationOverview />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "create-valuation",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Create
          ) ? (
            <BlackScholes />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "edit-valuation/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Edit
          ) ? (
            <BlackScholes />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "documents",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.All
          ) ? (
            <DocumentManager />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employeeSyncs",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <SyncDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employeeMetaData",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <EmployeeMetaData />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "syncDetails/:id",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <IndividualSyncDetails />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "pendingRequests",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <PendingRequestsApproval />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "employeeSyncs/pendingRequests",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <PendingRequestsApproval />
          ) : (
            <NotFound />
          ),
        },
      ],
    },
    {
      path: "/billing",
      element: isAuthenticated ? (
        isBillingUser ? (
          <OptionsLayout />
        ) : (
          <NotFound />
        )
      ) : (
        <RedirectLogin />
      ),
      children: [{ index: true, element: <BillingDashboard /> }],
    },
    {
      path: "/manager-portal",
      element: isAuthenticated ? (
        permission?.role === "MANAGER" ? (
          <OptionsLayout />
        ) : !permission?.role ? null : (
          <Navigate to="/options/esopOverview" replace />
        )
      ) : (
        <RedirectLogin />
      ),
      children: [
        {
          path: "dashboard",
          element: <ManagerDashboard />,
        },
      ],
    },
    {
      path: "/modelling",
      element: isAuthenticated ? <OptionsLayout /> : <RedirectLogin />,
      children: [
        { path: "home", element: <Modelling /> },
        { path: "companyDetails", element: <CompanyDetails /> },
        { path: "hiringPlan", element: <HiringPlan /> },
        { path: "compensationModelling", element: <CompensationModelling /> },
      ],
    },
    {
      path: "/settings",
      element: isAuthenticated ? <OptionsLayout /> : <RedirectLogin />,
      children: [
        {
          path: "companyProfile",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <CompanyProfileStepper />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "siteSettings",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <SiteSettings />
          ) : (
            <NotFound />
          ),
        },
        {
          path: "faq",
          element: determineUserAccessToResource(
            permission?.aclList || [],
            Resource.All,
            Action.Read
          ) ? (
            <FaqComponent />
          ) : (
            <NotFound />
          ),
        },
      ],
    },
    {
      path: "/auth",
      element: isAuthenticated ? (
        determineUserAccessToResource(
          permission?.aclList || [],
          Resource.All,
          Action.Read
        ) ? (
          <OptionsLayout />
        ) : (
          <NotFound />
        )
      ) : (
        <RedirectLogin />
      ),
      children: [{ path: "usersRoles", element: <UserRoles /> }],
    },
    {
      path: "/allPlans",
      element: <AllPlans />,
    },
    {
      path: "/404",
      element: <NotFound />,
    },
    { path: "*", element: <Navigate to="/404" replace /> },
    {
      path: "/",
      element: !isAuthenticated ? (
        <RedirectLogin />
      ) : !permission?.role ? null : permission.role === "MANAGER" ? (
        <Navigate to="/manager-portal/dashboard" replace />
      ) : (
        <Navigate to="/options/esopOverview" replace />
      ),
    },
    {
      path: "/settings",
      element: <Navigate to="/settings/siteSettings" replace />,
    },
    {
      path: "/user",
      element: isAuthenticated ? <OptionsLayout /> : <RedirectLogin />,
      children: [{ path: "profile", element: <UserProfilePage /> }],
    },
  ]);

  if (isPublicRoute) {
    return routes;
  }
  if (optionsAdminAuth) {
    if (!accessToken) return <RedirectLogin />;
    if (localSession.isError) {
      if ((localSession.error as AxiosError)?.response?.status === 401) {
        return <RedirectLogin />;
      }
      return (
        <RequestError
          title="Unable to verify your session"
          onRetry={() => localSession.refetch()}
        />
      );
    }
    if (!localSession.data) return <Loader />;
  }
  if (!optionsAdminAuth && accessTokenFailed) {
    if (isEquionSessionInvalid(accessTokenError)) return <Loader />;
    return (
      <RequestError
        title="Unable to sign in"
        onRetry={() => {
          retryAccessToken();
        }}
      />
    );
  }
  if (!hasBootstrappedSession) {
    return <Loader />;
  }
  if (shouldCheckHeartbeat && heartbeatFailed) {
    if (!isEquionSessionInvalid(heartbeatError)) {
      return (
        <RequestError
          title="Unable to verify your session"
          onRetry={() => {
            retryHeartbeat();
          }}
        />
      );
    }
    return <Loader />;
  }
  if (shouldCheckHeartbeat && !heartbeatData) return <Loader />;
  if (
    contextFetchedForGrant === verifiedGrant &&
    (userContextFailed || !userContext)
  ) {
    return (
      <RequestError
        title="Unable to load your account"
        onRetry={() => {
          if (verifiedGrant) loadUserContext(verifiedGrant);
        }}
      />
    );
  }
  if (contextFetchedForGrant !== verifiedGrant) return <Loader />;
  if (
    permissionFetchedForGrant === verifiedGrant &&
    (permissionFailed || !permissionData?.role)
  ) {
    return (
      <RequestError
        title="Unable to load permissions"
        onRetry={() => {
          if (verifiedGrant) loadPermission(verifiedGrant);
        }}
      />
    );
  }
  if (permissionFetchedForGrant !== verifiedGrant || !permission?.role) {
    return <Loader />;
  }

  return (
    <OperationalControlAdmissionGate>
      {!optionsAdminAuth && <PendingHissaCompanySwitch />}
      {routes}
    </OperationalControlAdmissionGate>
  );
}
function isAllowed(route: string) {
  // check from user object permissions
  // ideally a call
  return true;
}
